//
// chart.js
//

import {
  ArcElement,
  BarController,
  BarElement,
  BubbleController,
  CategoryScale,
  Chart,
  Decimation,
  DoughnutController,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PieController,
  PointElement,
  PolarAreaController,
  RadarController,
  RadialLinearScale,
  ScatterController,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip,
} from 'chart.js';

import { getCSSVariableValue, getGradient } from './helpers';

Chart.register(
  ArcElement,
  BarController,
  BarElement,
  BubbleController,
  CategoryScale,
  Decimation,
  DoughnutController,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  LogarithmicScale,
  PieController,
  PointElement,
  PolarAreaController,
  RadarController,
  RadialLinearScale,
  ScatterController,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip,
);

export const colors = {
  bodySecondary: getCSSVariableValue('--bs-secondary-color'),
  tertiaryBg: getCSSVariableValue('--bs-tertiary-bg'),
  border: getCSSVariableValue('--bs-border-color'),
  primary: getCSSVariableValue('--bs-primary'),
  primaryTranslucent: `rgba(${getCSSVariableValue('--bs-primary-rgb')}, .05)`,
  transparent: 'rgba(255, 255, 255, 0)',
};

const fonts = {
  base: 'Inter',
};

//
// Functions
//

function globalOptions() {
  Chart.defaults.responsive = true;
  Chart.defaults.maintainAspectRatio = false;

  // Default
  Chart.defaults.color = colors.bodySecondary;
  Chart.defaults.font.family = fonts.base;
  Chart.defaults.font.size = 13;

  // Layout
  Chart.defaults.layout.padding = 0;

  // Legend
  Chart.defaults.plugins.legend.display = false;

  // Point
  Chart.defaults.elements.point.radius = 0;
  Chart.defaults.elements.point.backgroundColor = colors.primary;

  // Line
  Chart.defaults.elements.line.tension = 0.4;
  Chart.defaults.elements.line.borderWidth = 2;
  Chart.defaults.elements.line.borderColor = colors.primary;
  Chart.defaults.elements.line.backgroundColor = (context) => {
    const chart = context.chart;
    const { ctx, chartArea } = chart;
    if (!chartArea) {
      return;
    }
    return getGradient(ctx, chartArea, [colors.transparent, colors.primaryTranslucent, colors.primaryTranslucent]);
  };
  Chart.defaults.elements.line.borderCapStyle = 'rounded';
  Chart.defaults.elements.line.fill = true;

  // Bar
  Chart.defaults.elements.bar.backgroundColor = colors.primary;
  Chart.defaults.elements.bar.borderWidth = 0;
  Chart.defaults.elements.bar.borderRadius = 6;
  Chart.defaults.elements.bar.borderSkipped = false;
  Chart.defaults.datasets.bar.maxBarThickness = 12;

  // Arc
  Chart.defaults.elements.arc.backgroundColor = colors.primary;
  Chart.defaults.elements.arc.borderWidth = 4;
  Chart.defaults.elements.arc.borderColor = colors.tertiaryBg;
  Chart.defaults.elements.arc.hoverBorderColor = colors.tertiaryBg;

  // Tooltips (ponytail: removed dead externalTooltipHandler)
  Chart.defaults.plugins.tooltip.enabled = false;

  // Doughnut
  Chart.defaults.datasets.doughnut.cutout = '83%';

  // yAxis
  Chart.defaults.scales.linear.border = { ...Chart.defaults.scales.linear.border, ...{ display: false, dash: [3], dashOffset: [2] } };
  Chart.defaults.scales.linear.grid = {
    ...Chart.defaults.scales.linear.grid,
    ...{ color: colors.border, drawTicks: false },
  };

  Chart.defaults.scales.linear.beginAtZero = true;
  Chart.defaults.scales.linear.ticks.padding = 10;
  Chart.defaults.scales.linear.ticks.stepSize = 10;

  // xAxis
  Chart.defaults.scales.category.border = { ...Chart.defaults.scales.category.border, ...{ display: false } };
  Chart.defaults.scales.category.grid = { ...Chart.defaults.scales.category.grid, ...{ display: false, drawTicks: false, drawOnChartArea: false } };
  Chart.defaults.scales.category.ticks.padding = 20;
  Chart.defaults.scales.category.ticks.autoSkip = true;
  Chart.defaults.scales.category.ticks.maxRotation = 0;
  Chart.defaults.scales.category.ticks.minRotation = 0;

  // Time axis
  Chart.defaults.scales.time.border = { ...Chart.defaults.scales.category.border, ...{ display: false } };
  Chart.defaults.scales.time.grid = { ...Chart.defaults.scales.category.grid, ...{ display: false, drawTicks: false, drawOnChartArea: false } };
  Chart.defaults.scales.time.ticks.padding = 20;
  Chart.defaults.scales.time.ticks.autoSkip = true;
  Chart.defaults.scales.time.ticks.maxRotation = 0;
  Chart.defaults.scales.time.ticks.minRotation = 0;
}

// ponytail: removed dead externalTooltipHandler + getOrCreateTooltip (~150 lines)
// Admin charts use inline tooltip callbacks with {enabled: true} per-chart.

//
// Events
//

globalOptions();

// Make available globally for users of the /dist folder
// Feel free to comment this out if you're working in the /src folder
window.Chart = Chart;
